import { computed, inject, Injectable, WritableSignal } from "@angular/core"
import { Profile, ProfileField, ProfileProgress, ProfileStatus } from "./profile.model"
import { RouteService } from "../../services/route.service"
import { enumStringKeys } from "util/enum-keys"
import { FirebaseAuthService } from "../../auth/firebase-auth.service"
import { FirebaseAuthProfileService } from "../../auth/firebase-auth-profile.service"
import { RegionRoles, RolesEnum } from "redlineLibs"

// export enum Role {
//   ADMIN = "ADMIN",
//   BLOCKED = "BLOCKED",
//   USER_ADMIN = "USER_ADMIN",
//   CONTRIBUTOR = "CONTRIBUTOR",
//   EDITOR = "EDITOR",
//   MODERATOR = "MODERATOR",
// }

@Injectable({
  providedIn: "root",
})
export class ProfileService {
  private routeService = inject(RouteService)
  private firebaseAuthService = inject(FirebaseAuthService)
  private firebaseAuthProfileService = inject(FirebaseAuthProfileService)

  /**
   * providing userService signals so that components
   * won't have to inject both profile and user services
   */
  idTokenResultWithClaims = this.firebaseAuthService.idTokenResultWithClaims
  afUser = this.firebaseAuthService.afUser
  userInit = this.firebaseAuthService.userInit
  userId = this.firebaseAuthService.userId
  loggedIn = this.firebaseAuthService.loggedIn
  region = this.routeService.region
  // claimsRoles = computed<RolesEnum[]>(() => this.firebaseAuthService.claimsRoles())
  claimsRegionRoles = computed<RegionRoles>(() => this.firebaseAuthService.claimsRegionRoles())
  currentRegionRoles = computed<RolesEnum[]>(() => {
    const roles = this.claimsRegionRoles()[this.region()] || []
    if (this.firebaseAuthService.isAdmin()) {
      return [
        ...roles,
        RolesEnum.ADMIN,
      ]
    }
    return roles
  })


  profile = this.firebaseAuthProfileService.profile as WritableSignal<Profile | undefined>

  userName = computed(() =>
    this.firebaseAuthProfileService.profile()?.userName
    || this.firebaseAuthService.providerData().displayName
    || this.firebaseAuthService.afUserDisplayName()
    || this.firebaseAuthService.providerData().email
    || "",
  )

  getUserRole(): RolesEnum {
    return this.isAdmin() ?
      RolesEnum.ADMIN :
      this.isEditor()
        ? RolesEnum.EDITOR :
        this.isModerator()
          ? RolesEnum.MODERATOR :
          this.isUserAdmin()
            ? RolesEnum.USER_ADMIN :
            this.isContributor()
              ? RolesEnum.CONTRIBUTOR :
              RolesEnum.CONTRIBUTOR
  }

  profileStatus = computed(() => this.getProfileStatus(this.profile()))

  hasRole = computed(() =>
    this.mapRoles(
      this.idTokenResultWithClaims()?.claims?.["roles"] as RolesEnum[] || [],
      this.profileStatus(),
    ),
  )
  hasRegionRole = computed(() =>
    this.mapRoles(
      this.idTokenResultWithClaims()?.claims?.["regionRoles"]?.[this.region()] as RolesEnum[] || [],
      this.profileStatus(),
    ),
  )
  hasRoles = computed(() => {
    const hasRoles = [] as RolesEnum[]
    for (const [role, value] of Object.entries(this.hasRole())) {
      if (value) hasRoles.push(role as RolesEnum)
    }
    return hasRoles
  })

  isAdmin = computed(() =>
    this.hasRole().ADMIN
  )
  isUserAdmin = computed(() =>
    this.hasRegionRole().USER_ADMIN || this.isAdmin()
  )
  isEditor = computed(() =>
    this.hasRegionRole().EDITOR || this.isAdmin()
  )
  isModerator = computed(() =>
    this.hasRegionRole().MODERATOR || this.isAdmin() || this.isEditor()
  )
  isContributor = computed(() =>
    this.hasRegionRole().CONTRIBUTOR  || this.isAdmin() || this.isEditor()|| this.isModerator() || this.isUserAdmin()
  )
  isBlocked = computed(() =>
    this.hasRegionRole().BLOCKED
  )
  isKiosk = computed(() =>
    this.hasRegionRole().KIOSK
  )

  homeLocation = computed(() => this.profile()?.homeLocation || "")
  birthYear = computed(() => this.profile()?.birthYear || 0)
  title = computed(() => this.profile()?.title || "")
  pronouns = computed(() => this.profile()?.pronouns || [])
  shortBio = computed(() => this.profile()?.shortBio || "")
  issues = computed(() => this.profile()?.issues || [])
  races = computed(() => this.profile()?.races || [])
  genders = computed(() => this.profile()?.genders || [])
  sleepLocations = computed(() => this.profile()?.sleepLocations || [])
  educations = computed(() => this.profile()?.educations || [])
  householdIncomeRanges = computed(() => this.profile()?.householdIncomeRanges || [])
  profileImage = computed(() => this.profile()?.profileImage)

  // saveToProfileField(fieldName: ProfileField, value: unknown) {
  //   this.userLoggedInService.saveToProfileField(fieldName, value)
  // }

  // saveToProfile(profile: Profile | undefined) {
  //   if (profile) {
  //     this.profileEditorService.saveToProfile(profile)
  //   }
  // }

  getProfileStatus(profile: Profile | undefined): ProfileStatus {
    let profileProgress = ProfileProgress.INCOMPLETE
    let completedCount = 0
    let requiredCount = 0

    if (profile) {
      enumStringKeys(ProfileField).map(fieldName => {
        const value = profile[fieldName]
        const currentYear = new Date(Date.now()).getFullYear()
        switch (fieldName) {
          case "birthYear": {
            const birthYear = value as number | undefined
            if (birthYear && birthYear > 1900 && birthYear < currentYear) {
              completedCount++
              requiredCount++
            }
          }
            break
          case "homeLocation":
          case "title":
          case "userName":
            if (value) {
              completedCount++
              requiredCount++
            }
            break
          case "shortBio": {
            const shortBio = value as string | undefined
            if (shortBio) {
              completedCount++
            }
          }
            break
          case "issues":
          case "pronouns":
          case "educations":
          case "genders":
          case "householdIncomeRanges":
          case "races":
          case "sleepLocations": {
            const values = value as string[] | undefined
            if (values?.find(item => item.length)) {
              completedCount++
            }
          }
            break
          case "profileImage":
            break
        }
      })
      // console.log("requiredCount, need exactly 4 to set REQUIRED", requiredCount)
      if (requiredCount === 4) {
        profileProgress = ProfileProgress.REQUIRED
      }
      // console.log("add 1 to completedCount if true", profile.profileImage?.filePath)
      if (profile.profileImage?.filePath) {
        completedCount++
      }
      // console.log("completedCount, need exactly 13 to set COMPLETE", completedCount)
      if (completedCount === 13) {
        profileProgress = ProfileProgress.COMPLETE
      }
    }
    const completedPercent = Math.floor(completedCount / 13 * 10) * 10

    return {
      percent: completedPercent,
      progress: profileProgress,
      type: "circle",
    }
  }

  mapRoles(customClaimsRoles: RolesEnum[], profileStatus: ProfileStatus): { [K in RolesEnum]: boolean } {
    const allRoles: RolesEnum[] = enumStringKeys(RolesEnum)
    const hasRole = {} as { [K in RolesEnum]: boolean }
    /**
     * set true for all roles in custom claims
     * set false for all the rest in allRoles
     */
    allRoles.map(role => {
      hasRole[role] = !!customClaimsRoles.find(customClaimsRole => customClaimsRole === role)
    })
    /**
     * add roles if has parent role
     * for example, Admin gets all roles
     */
    allRoles.map(role => {
      switch (role) {
        case RolesEnum.ADMIN:
          if (hasRole[role]) {
            hasRole[RolesEnum.USER_ADMIN] = true
            hasRole[RolesEnum.EDITOR] = true
            hasRole[RolesEnum.MODERATOR] = true
            hasRole[RolesEnum.CONTRIBUTOR] = true
          }
          break
        case RolesEnum.EDITOR:
          if (hasRole[role]) {
            hasRole[RolesEnum.MODERATOR] = true
            hasRole[RolesEnum.CONTRIBUTOR] = true
          }
          break
        case RolesEnum.MODERATOR:
          if (hasRole[role]) {
            hasRole[RolesEnum.CONTRIBUTOR] = true
          }
          break
        case RolesEnum.CONTRIBUTOR:
          switch (profileStatus.progress) {
            case ProfileProgress.INCOMPLETE:
              // case ProfileProgress.EXCEPTION:
              break
            case ProfileProgress.REQUIRED:
            case ProfileProgress.COMPLETE:
              hasRole[RolesEnum.CONTRIBUTOR] = true
              break
          }
      }
    })
    /**
     * Remove all other roles if has Blocked role
     */
    allRoles.map(role => {
      switch (role) {
        case RolesEnum.BLOCKED:
          if (hasRole[role]) {
            hasRole[RolesEnum.KIOSK] = false
            hasRole[RolesEnum.USER_ADMIN] = false
            hasRole[RolesEnum.ADMIN] = false
            hasRole[RolesEnum.EDITOR] = false
            hasRole[RolesEnum.MODERATOR] = false
            hasRole[RolesEnum.CONTRIBUTOR] = false
          }
          break
      }
    })
    /**
     * Remove all other roles if has KIOSK role
     */
    allRoles.map(role => {
      switch (role) {
        case RolesEnum.KIOSK:
          if (hasRole[role]) {
            hasRole[RolesEnum.BLOCKED] = false
            hasRole[RolesEnum.USER_ADMIN] = false
            hasRole[RolesEnum.ADMIN] = false
            hasRole[RolesEnum.EDITOR] = false
            hasRole[RolesEnum.MODERATOR] = false
            hasRole[RolesEnum.CONTRIBUTOR] = false
          }
          break
      }
    })
    return hasRole
  }

}
